// =============================================================================
// Anomaly engine — pure rules over a UserActivityBundle / TeamActivityBundle.
// Each rule is a `(bundle, ctx) => Anomaly[]` function. Composing all rules
// for a subject + sorting by weight gives the AnomaliesCard's list and feeds
// the RiskScore composer.
//
// Rules (D.1–D.13) come from the dashboard plan. Each rule emits zero or
// more anomalies; an anomaly carries `evidence.event_ids` (indices into the
// bundle's events array) so the UI can scroll/highlight the matching panel.
//
// `ctx` carries cross-subject signals the rules can't compute alone:
//   - orgDomainsSeen: Set<string>      — every external domain ever shared to
//   - userPriorLastActive: number      — last_active ts before the active range
//   - userPrimaryCountry: string       — the user's home country
// SDK pushes the same shape in production.
// =============================================================================

// Tunables — kept at module scope so they're easy to override per-tenant later.
const HIGH_PRIV_TOKENS = /admin|exec|sec(urity)?-(admin|lead)/i;
const FRESH_DOMAIN_CAP = 30;       // max combined weight from D.6
const NEW_COUNTRY_CAP  = 20;       // max combined weight from D.2
const OFF_HOURS_THRESHOLD = 0.30;
const OFF_HOURS_MIN_TOTAL = 20;
const SHARE_RATE_PER_HOUR = 5;
const SHARE_RATE_MIN_TOTAL = 10;
const BULK_DELETE_PER_HOUR = 10;
const DENIED_THEN_ALLOWED_MIN = 3;
const DENIED_THEN_ALLOWED_WINDOW_MS = 60 * 60 * 1000;
const DOWNLOAD_VOLUME_BYTES = 1024 ** 3; // 1 GB threshold (proxy)

// Helpers ─────────────────────────────────────────────────────────────────────

const _hour = (ts) => Math.floor(ts / 3_600_000);
const _isExternalShare = (e) =>
  (e.operation === "Share" || e.operation === "UpdateShare") &&
  (e.subject?.share?.acl?.type_of_path === "TShareExternal" ||
   e.subject?.updateShare?.acl?.type_of_path === "TShareExternal" ||
   /external/i.test(e.message || ""));

// Bytes-out heuristic mirroring _buildFilesTouched: chunk reads × file size,
// capped at file size. Real backend will replace with per-chunk transfer.
const _bytesOutFor = (touched) => (touched || []).reduce((a, t) => a + (t.bytes_out || 0), 0);

const _stableId = (kind, ...parts) => `${kind}:${parts.join("|")}`;

// ── D.1 Multi-country same-hour access ──────────────────────────────────────
function multiCountrySameHour(bundle) {
  const buckets = {};
  bundle.events.forEach((e, i) => {
    if (!e.country) return;
    const h = _hour(e.timestamp);
    if (!buckets[h]) buckets[h] = { countries: new Map(), evidence: [] };
    buckets[h].countries.set(e.country, true);
    buckets[h].evidence.push(i);
  });
  const out = [];
  Object.entries(buckets).forEach(([h, b]) => {
    if (b.countries.size >= 2) {
      const cs = [...b.countries.keys()];
      out.push({
        id: _stableId("D1", h, cs.join(",")),
        kind: "multi_country_same_hour",
        severity: "high",
        weight: 25,
        title: "Multi-country access in the same hour",
        description: `Accessed from ${cs.join(" + ")} within one hour`,
        evidence: { event_ids: b.evidence, panel: "where" },
        first_seen: bundle.events[b.evidence[0]].timestamp,
        last_seen: bundle.events[b.evidence[b.evidence.length - 1]].timestamp,
        count: b.evidence.length,
      });
    }
  });
  return out;
}

// ── D.2 New country first-seen ──────────────────────────────────────────────
function newCountryFirstSeen(bundle, ctx) {
  const everSeen = new Set(ctx?.userPriorCountries || []);
  const fresh = [];
  bundle.countries.forEach((c) => {
    if (!everSeen.has(c.country)) fresh.push(c);
  });
  if (!fresh.length) return [];
  const evIdsFor = (country) => bundle.events
    .map((e, i) => (e.country === country ? i : -1))
    .filter((i) => i >= 0);
  return fresh.slice(0, 3).map((c) => ({
    id: _stableId("D2", c.country),
    kind: "new_country_first_seen",
    severity: "medium",
    weight: Math.min(10, NEW_COUNTRY_CAP / Math.max(1, fresh.length)) | 0 || 10,
    title: `New country: ${c.country}`,
    description: `First-time access from ${c.country} (${c.count} event${c.count === 1 ? "" : "s"})`,
    evidence: { event_ids: evIdsFor(c.country), panel: "where" },
    first_seen: c.firstSeen,
    last_seen: c.lastSeen,
    count: c.count,
  }));
}

// ── D.3 Off-hours spike ─────────────────────────────────────────────────────
function offHoursSpike(bundle) {
  if (bundle.events.length < OFF_HOURS_MIN_TOTAL) return [];
  const total = bundle.events.length;
  const evidence = [];
  let off = 0;
  bundle.events.forEach((e, i) => {
    const h = new Date(e.timestamp).getUTCHours();
    if (h < 8 || h >= 20) { off += 1; evidence.push(i); }
  });
  const ratio = off / total;
  if (ratio <= OFF_HOURS_THRESHOLD) return [];
  return [{
    id: _stableId("D3"),
    kind: "off_hours_spike",
    severity: "medium",
    weight: 10,
    title: "Off-hours activity spike",
    description: `${off} of ${total} events fell outside 8am–8pm (${(ratio * 100).toFixed(1)}%)`,
    evidence: { event_ids: evidence, panel: "when" },
    first_seen: bundle.events[evidence[0]]?.timestamp || 0,
    last_seen: bundle.events[evidence[evidence.length - 1]]?.timestamp || 0,
    count: off,
  }];
}

// ── D.4 High download volume ────────────────────────────────────────────────
function highDownloadVolume(bundle) {
  const bytes = _bytesOutFor(bundle.filesTouched);
  if (bytes <= DOWNLOAD_VOLUME_BYTES) return [];
  const evidence = bundle.events.map((e, i) => (e.operation === "GetChunk" ? i : -1)).filter((i) => i >= 0);
  return [{
    id: _stableId("D4"),
    kind: "high_download_volume",
    severity: "high",
    weight: 30,
    title: "High download volume",
    description: `${(bytes / (1024 ** 3)).toFixed(2)} GB read across ${evidence.length} chunk request${evidence.length === 1 ? "" : "s"}`,
    evidence: { event_ids: evidence, panel: "files_touched" },
    first_seen: bundle.events[evidence[0]]?.timestamp || 0,
    last_seen: bundle.events[evidence[evidence.length - 1]]?.timestamp || 0,
    count: evidence.length,
  }];
}

// ── D.5 High share-creation rate ────────────────────────────────────────────
function highShareRate(bundle) {
  const shares = (bundle.sharesGranted || []);
  if (shares.length < SHARE_RATE_MIN_TOTAL) return [];
  const buckets = {};
  shares.forEach((s) => {
    const h = _hour(s.at);
    if (!buckets[h]) buckets[h] = 0;
    buckets[h] += 1;
  });
  const peak = Math.max(0, ...Object.values(buckets));
  if (peak <= SHARE_RATE_PER_HOUR) return [];
  const evidence = bundle.events
    .map((e, i) => ((e.operation === "Share" || e.operation === "UpdateShare") ? i : -1))
    .filter((i) => i >= 0);
  return [{
    id: _stableId("D5"),
    kind: "high_share_rate",
    severity: "medium",
    weight: 15,
    title: "High share-creation rate",
    description: `${peak} share ops in a single hour (total ${shares.length} in window)`,
    evidence: { event_ids: evidence, panel: "shares_granted" },
    first_seen: bundle.events[evidence[0]]?.timestamp || 0,
    last_seen: bundle.events[evidence[evidence.length - 1]]?.timestamp || 0,
    count: shares.length,
  }];
}

// ── D.6 External share to fresh domain ──────────────────────────────────────
function externalShareToFreshDomain(bundle, ctx) {
  const seen = new Set(ctx?.orgDomainsSeen || []);
  const freshHits = (bundle.sharesGranted || [])
    .filter((s) => s.recipient_type === "external" && s.external_domain)
    .filter((s) => !seen.has(s.external_domain));
  if (!freshHits.length) return [];
  const totalWeight = Math.min(FRESH_DOMAIN_CAP, freshHits.length * 20);
  return freshHits.slice(0, 3).map((s) => {
    const evIdx = bundle.events.findIndex((e) => e.timestamp === s.at && e.user_email === s.actor_email);
    return {
      id: _stableId("D6", s.external_domain),
      kind: "external_share_fresh_domain",
      severity: "high",
      weight: totalWeight / Math.min(3, freshHits.length),
      title: `Share to fresh domain: ${s.external_domain}`,
      description: `${s.object_path} shared with ${s.recipient || s.external_domain} — domain has no prior history`,
      evidence: { event_ids: evIdx >= 0 ? [evIdx] : [], panel: "shares_granted" },
      first_seen: s.at,
      last_seen: s.at,
      count: 1,
    };
  });
}

// ── D.7 Denied-then-allowed ─────────────────────────────────────────────────
function deniedThenAllowed(bundle) {
  const byKey = {};
  bundle.events.forEach((e, i) => {
    if (!e.object_uuid) return;
    const key = `${e.user_email}|${e.object_uuid}`;
    if (!byKey[key]) byKey[key] = { denials: [], firstAllow: -1, denialIdx: [] };
    if (e.success === false) {
      byKey[key].denials.push(e);
      byKey[key].denialIdx.push(i);
    } else if (e.success === true && byKey[key].denials.length >= DENIED_THEN_ALLOWED_MIN) {
      const lastDeny = byKey[key].denials[byKey[key].denials.length - 1];
      if (e.timestamp - lastDeny.timestamp <= DENIED_THEN_ALLOWED_WINDOW_MS && byKey[key].firstAllow < 0) {
        byKey[key].firstAllow = i;
      }
    }
  });
  const out = [];
  Object.entries(byKey).forEach(([key, v]) => {
    if (v.firstAllow >= 0) {
      const evidence = [...v.denialIdx, v.firstAllow];
      out.push({
        id: _stableId("D7", key),
        kind: "denied_then_allowed",
        severity: "high",
        weight: 20,
        title: "Denied then allowed (bypass)",
        description: `${v.denials.length} denial${v.denials.length === 1 ? "" : "s"} on the same object, then a success within an hour`,
        evidence: { event_ids: evidence, panel: "denied" },
        first_seen: v.denials[0].timestamp,
        last_seen: bundle.events[v.firstAllow].timestamp,
        count: v.denials.length,
      });
    }
  });
  return out;
}

// ── D.8 Role-grant escalation ───────────────────────────────────────────────
// Detects when *this* user gains a high-priv role/team during the active
// range. Reads from bundle.permissionsDelta.added (synth in mock; SDK driven
// in prod).
function roleEscalation(bundle) {
  const added = bundle.permissionsDelta?.added || [];
  const escalations = added.filter(
    (e) => (e.role && HIGH_PRIV_TOKENS.test(e.role)) || (e.team && HIGH_PRIV_TOKENS.test(e.team)),
  );
  if (!escalations.length) return [];
  return escalations.map((e) => ({
    id: _stableId("D8", e.role || e.team),
    kind: "role_escalation",
    severity: "high",
    weight: 25,
    title: e.role ? `Role escalation: ${e.role}` : `Team escalation: ${e.team}`,
    description: `Newly granted ${e.role ? "role" : "team membership"} carrying high privileges`,
    evidence: { event_ids: [], panel: "permissions" },
    first_seen: e.granted_at,
    last_seen: e.granted_at,
    count: 1,
  }));
}

// ── D.9 Dormant account suddenly active ─────────────────────────────────────
function dormantSuddenlyActive(bundle, ctx) {
  if (!bundle.events.length) return [];
  const prior = ctx?.userPriorLastActive || 0;
  const firstInRange = bundle.events[0].timestamp;
  if (!prior) return [];
  const gapDays = (firstInRange - prior) / 86_400_000;
  if (gapDays < 14) return [];
  return [{
    id: _stableId("D9"),
    kind: "dormant_suddenly_active",
    severity: "medium",
    weight: 15,
    title: "Dormant account became active",
    description: `Inactive for ${Math.round(gapDays)} days before this window`,
    evidence: { event_ids: [0], panel: "identity" },
    first_seen: firstInRange,
    last_seen: bundle.events[bundle.events.length - 1].timestamp,
    count: 1,
  }];
}

// ── D.10 Concurrent sessions disjoint countries ─────────────────────────────
// Approximation without proper sessionization: pairs of events from the same
// user_email within ≤ 30min of each other but from different countries that
// are not in the same trust tier. Real session math lands in PR4.
function concurrentDisjoint(bundle) {
  const out = [];
  for (let i = 0; i < bundle.events.length; i++) {
    const a = bundle.events[i];
    if (!a.country) continue;
    for (let j = i + 1; j < bundle.events.length; j++) {
      const b = bundle.events[j];
      if (b.timestamp - a.timestamp > 30 * 60_000) break;
      if (a.user_email !== b.user_email) continue;
      if (!b.country || a.country === b.country) continue;
      out.push({
        id: _stableId("D10", a.timestamp, a.country, b.country),
        kind: "concurrent_disjoint",
        severity: "high",
        weight: 25,
        title: "Concurrent access from disjoint countries",
        description: `${a.country} and ${b.country} within 30 minutes`,
        evidence: { event_ids: [i, j], panel: "where" },
        first_seen: a.timestamp,
        last_seen: b.timestamp,
        count: 2,
      });
      break; // one anomaly per outer event is enough — avoids O(n²) feed
    }
  }
  return out;
}

// ── D.11 Bulk delete ────────────────────────────────────────────────────────
const _DESTRUCTIVE_OPS = new Set([
  "RemoveVersion", "RemoveComment", "RemoveOrganizationUser",
  "RemoveRole", "RemoveTeam", "BlockUser",
]);
function bulkDelete(bundle) {
  const buckets = {};
  bundle.events.forEach((e, i) => {
    if (!_DESTRUCTIVE_OPS.has(e.operation)) return;
    const h = _hour(e.timestamp);
    if (!buckets[h]) buckets[h] = [];
    buckets[h].push(i);
  });
  const peak = Object.entries(buckets).find(([, ids]) => ids.length > BULK_DELETE_PER_HOUR);
  if (!peak) return [];
  const [, ids] = peak;
  return [{
    id: _stableId("D11", peak[0]),
    kind: "bulk_delete",
    severity: "high",
    weight: 25,
    title: "Bulk destructive operations",
    description: `${ids.length} delete-class operations in a single hour`,
    evidence: { event_ids: ids, panel: "timeline" },
    first_seen: bundle.events[ids[0]].timestamp,
    last_seen: bundle.events[ids[ids.length - 1]].timestamp,
    count: ids.length,
  }];
}

// ── D.12 Foreign-only writes ────────────────────────────────────────────────
const _WRITE_OPS = new Set(["StartWrite", "FinalizeWrite", "RestoreVersion"]);
function foreignOnlyWrites(bundle, ctx) {
  const home = ctx?.userPrimaryCountry;
  if (!home) return [];
  const writes = [];
  bundle.events.forEach((e, i) => {
    if (_WRITE_OPS.has(e.operation)) writes.push({ e, i });
  });
  if (writes.length < 3) return [];
  const allForeign = writes.every(({ e }) => e.country && e.country !== home);
  if (!allForeign) return [];
  return [{
    id: _stableId("D12"),
    kind: "foreign_only_writes",
    severity: "medium",
    weight: 15,
    title: "All writes from foreign locations",
    description: `${writes.length} write operation${writes.length === 1 ? "" : "s"}, none from ${home}`,
    evidence: { event_ids: writes.map((w) => w.i), panel: "where" },
    first_seen: writes[0].e.timestamp,
    last_seen: writes[writes.length - 1].e.timestamp,
    count: writes.length,
  }];
}

// ── D.13 Self-grant chain on confidential ──────────────────────────────────
const _SENSITIVE_TAGS = new Set(["confidential", "pii", "secret"]);
function selfGrantOnConfidential(bundle) {
  const hits = (bundle.sharesGranted || []).filter((s) => {
    if (s.recipient_type !== "external") return false;
    if (!s.granted_by?.is_self) return false;
    // Look up the file's tags via path → object touched. Cheap because
    // bundle.filesTouched already has tags resolved.
    const touched = (bundle.filesTouched || []).find((t) => t.path === s.object_path);
    if (!touched || !touched.tags) return false;
    return touched.tags.some((tag) => _SENSITIVE_TAGS.has(tag));
  });
  if (!hits.length) return [];
  return hits.map((s) => ({
    id: _stableId("D13", s.object_path, s.external_domain || s.recipient || ""),
    kind: "self_grant_on_confidential",
    severity: "high",
    weight: 30,
    title: "Self-granted external share on sensitive object",
    description: `${s.object_path} shared externally to ${s.recipient || s.external_domain}, granted by self`,
    evidence: { event_ids: [], panel: "shares_granted" },
    first_seen: s.at,
    last_seen: s.at,
    count: 1,
  }));
}

// ── Composer ────────────────────────────────────────────────────────────────

const RULES = [
  multiCountrySameHour,
  newCountryFirstSeen,
  offHoursSpike,
  highDownloadVolume,
  highShareRate,
  externalShareToFreshDomain,
  deniedThenAllowed,
  roleEscalation,
  dormantSuddenlyActive,
  concurrentDisjoint,
  bulkDelete,
  foreignOnlyWrites,
  selfGrantOnConfidential,
];

// Given a bundle + the cross-subject context, compute every anomaly. Returns
// a flat array sorted by weight descending so the AnomaliesCard can render
// straight from it without further sorting.
export function detectAnomalies(bundle, ctx = {}) {
  if (!bundle || !bundle.events) return [];
  const out = [];
  for (const rule of RULES) {
    try {
      const items = rule(bundle, ctx) || [];
      for (const a of items) out.push(a);
    } catch {
      // A single misbehaving rule must not break the whole feed.
      // Real telemetry would log here; for the dashboard we just skip.
    }
  }
  return out.sort((a, b) => b.weight - a.weight);
}

export const ANOMALY_KINDS = RULES.map((fn) => fn.name);
